/**
 * Roku "in-stock first" split scroll.
 *
 * For the targeted sorts (price asc/desc, best-selling, featured/manual) the
 * collection grid is driven as two chained streams via Shopify's native
 * availability filter:
 *   1. filter.v.availability=1  → every in-stock product, in the chosen sort
 *   2. filter.v.availability=0  → every out-of-stock product, in the chosen sort
 *
 * When stream 1 is exhausted the loader bridges into stream 2, so sold-out
 * cards always sink to the very bottom regardless of price/sort. The browser
 * URL stays clean and the toolbar "X products" counter (rendered server-side
 * from the unfiltered collection) keeps showing the full total.
 *
 * Activation is gated by `#ProductsList[data-split-mode="true"]`, which the
 * section only emits for the targeted sorts AND when the shopper has not
 * selected the availability filter themselves. The default sort and explicit
 * availability filtering fall back to the theme's normal infinite scroll.
 */
(function () {
  const SENTINEL_MARGIN = '700px 0px';
  const MAX_PAGES_PER_STREAM = 400; // hard safety stop

  let activeLoader = null;

  class StockSplitLoader {
    constructor(grid) {
      this.grid = grid; // #ProductsList (.products-list)
      this.sort = grid.dataset.splitSort || '';
      this.perPage = parseInt(grid.dataset.splitPerPage || '0', 10) || null;
      this.sectionId = grid.closest('.shopify-section')?.id?.replace('shopify-section-', '') || null;

      this.stream = 1; // 1 = in-stock, 0 = out-of-stock
      this.page = 1;
      this.pagesThisStream = 0;
      this.replaced = false; // have we swapped out the server-rendered grid yet?
      this.loading = false;
      this.done = false;

      this.sentinel = null;
      this.io = null;
    }

    buildUrl() {
      // Start from the live URL so any other active facets (set, type, price…)
      // are preserved; we only own sort + availability + page + section_id.
      const url = new URL(window.location.href);
      if (this.sort) url.searchParams.set('sort_by', this.sort);
      url.searchParams.set('filter.v.availability', String(this.stream));
      url.searchParams.set('page', String(this.page));
      if (this.sectionId) url.searchParams.set('section_id', this.sectionId);
      return url.toString();
    }

    start() {
      // Disarm the theme's default load-more button so it can't fetch mixed pages.
      this.removeDefaultPagination();
      this.setupSentinel();
      this.loadNext();
    }

    destroy() {
      if (this.io) this.io.disconnect();
      if (this.sentinel && this.sentinel.parentNode) this.sentinel.remove();
      this.done = true;
    }

    removeDefaultPagination() {
      const pag = document.querySelector('#ProductGridContainer .pagination');
      if (pag) pag.remove();
    }

    setupSentinel() {
      this.sentinel = document.createElement('div');
      this.sentinel.className = 'mz-stock-split-sentinel';
      this.sentinel.setAttribute('aria-hidden', 'true');
      this.sentinel.style.cssText = 'width:100%;height:1px;';
      this.grid.parentNode.insertBefore(this.sentinel, this.grid.nextSibling);

      this.io = new IntersectionObserver(
        (entries) => {
          if (entries.some((e) => e.isIntersecting)) this.loadNext();
        },
        { rootMargin: SENTINEL_MARGIN }
      );
      this.io.observe(this.sentinel);
    }

    async loadNext() {
      if (this.loading || this.done) return;
      this.loading = true;

      const requestedStream = this.stream;
      let html;
      try {
        const res = await fetch(this.buildUrl());
        if (!res.ok) throw new Error(`HTTP ${res.status} ${res.statusText} for ${this.buildUrl()}`);
        html = await res.text();
      } catch (err) {
        this.loading = false;
        // Surface the failure loudly; do not silently swallow.
        console.error('[stock-split] page fetch failed:', err);
        return;
      }

      const doc = new DOMParser().parseFromString(html, 'text/html');
      const srcGrid = doc.getElementById('ProductsList');
      const items = srcGrid ? Array.from(srcGrid.querySelectorAll('.f-column')) : [];
      const hasNext = this.detectHasNext(doc, items.length);

      // First batch with content swaps out the server-rendered (mixed) grid.
      if (items.length && !this.replaced) {
        this.grid.innerHTML = '';
        this.replaced = true;
      }
      items.forEach((item) => this.grid.appendChild(item));

      this.pagesThisStream += 1;
      const overCap = this.pagesThisStream >= MAX_PAGES_PER_STREAM;

      if (hasNext && !overCap) {
        this.page += 1;
      } else if (requestedStream === 1) {
        // In-stock exhausted → bridge into the out-of-stock stream.
        this.stream = 0;
        this.page = 1;
        this.pagesThisStream = 0;
      } else {
        // Out-of-stock exhausted → all done.
        this.destroy();
      }

      this.loading = false;

      // If the sentinel is still in view (short streams / tall viewport),
      // keep pulling so we don't stall waiting for a scroll.
      if (!this.done) {
        requestAnimationFrame(() => {
          if (!this.sentinel) return;
          const rect = this.sentinel.getBoundingClientRect();
          if (rect.top < window.innerHeight + 700) this.loadNext();
        });
      }
    }

    detectHasNext(doc, itemCount) {
      const pag = doc.querySelector('#ProductGridContainer .pagination');
      if (pag) {
        if (
          pag.querySelector('[is="load-more-button"]') ||
          pag.querySelector('button[action]') ||
          pag.querySelector('.pagination__item--next') ||
          pag.querySelector('a[rel="next"]')
        ) {
          return true;
        }
      }
      // Fallback: a full page implies there may be more.
      if (this.perPage && itemCount >= this.perPage) return true;
      return false;
    }
  }

  function teardownActive() {
    if (activeLoader) {
      activeLoader.destroy();
      activeLoader = null;
    }
  }

  function init() {
    teardownActive();
    const grid = document.querySelector('#ProductsList[data-split-mode="true"]');
    if (!grid) return;
    activeLoader = new StockSplitLoader(grid);
    activeLoader.start();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-initialise after facet/sort changes re-render the grid.
  document.addEventListener('collection:rerendered', init);
})();
